import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Trash2, Plus, AlertTriangle, Package } from 'lucide-react';
import Sidebar from '../../../components/ui/Sidebar';
import { useToast } from '../../../components/ui/Toast';
import { useConfirm } from '../../../components/ui/ConfirmDialog';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { useSecureAuth } from '../../../contexts/SecureAuthContext';
import useWastage from '../hooks/useWastage';
import './WastageTab.css';

/* ─── Module-scope helpers ─────────────────────────────────────── */

const REASONS = [
  { value: 'EXPIRED',  label: 'Expired',  hint: 'Past its date — most common' },
  { value: 'DAMAGED',  label: 'Damaged',  hint: 'Broken, spoiled, or unsellable' },
  { value: 'STOLEN',   label: 'Stolen',   hint: 'Theft / shrinkage' },
  { value: 'LOST',     label: 'Lost',     hint: 'Misplaced, can\'t find' },
  { value: 'RECALLED', label: 'Recalled', hint: 'Pulled from sale' },
  { value: 'OTHER',    label: 'Other',    hint: 'Anything else' },
];

const REASON_LABELS = REASONS.reduce((acc, r) => {
  acc[r.value] = r.label;
  return acc;
}, {});

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

/* ─── Module-scope sub-components (no inline defs) ─────────────── */

function SummaryStrip({ summary, formatMoney }) {
  return (
    <section className="fin-wt-summary">
      <div className="fin-wt-summary-row">
        <div className="fin-wt-summary-cell">
          <span className="fin-wt-summary-label">Logged events</span>
          <span className="fin-wt-summary-value">{summary.totalCount}</span>
        </div>
        <div className="fin-wt-summary-cell">
          <span className="fin-wt-summary-label">Units lost</span>
          <span className="fin-wt-summary-value">{summary.totalQuantity}</span>
        </div>
        <div className="fin-wt-summary-cell">
          <span className="fin-wt-summary-label">Total cost</span>
          <span className="fin-wt-summary-value fin-wt-summary-loss">
            {formatMoney(summary.totalLoss)}
          </span>
        </div>
      </div>
      {summary.byReason.length > 0 && (
        <div className="fin-wt-summary-chips">
          {summary.byReason.map((r) => (
            <span key={r.reason} className="fin-wt-chip">
              <span className="fin-wt-chip-label">{REASON_LABELS[r.reason] || r.reason}</span>
              <span className="fin-wt-chip-value">{r.quantity} · {formatMoney(r.totalLoss)}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function WastageRow({ row, onRemove, formatMoney }) {
  return (
    <article className="fin-wt-row">
      <div className="fin-wt-row-main">
        <p className="fin-wt-row-name">{row.productName}</p>
        <p className="fin-wt-row-meta">
          <span className={`fin-wt-reason fin-wt-reason--${row.reason.toLowerCase()}`}>
            {REASON_LABELS[row.reason] || row.reason}
          </span>
          <span className="fin-wt-dot">·</span>
          <span>{row.quantity} unit{Number(row.quantity) === 1 ? '' : 's'}</span>
          <span className="fin-wt-dot">·</span>
          <span>{formatDate(row.occurredAt)}</span>
        </p>
        {row.notes && <p className="fin-wt-row-notes">{row.notes}</p>}
      </div>
      <div className="fin-wt-row-right">
        <span className="fin-wt-row-loss">{formatMoney(row.totalLoss)}</span>
        <button
          type="button"
          className="fin-wt-row-delete"
          onClick={() => onRemove(row)}
          aria-label="Delete wastage entry"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </article>
  );
}

function ProductPicker({ query, setQuery, products, loading, value, onChange }) {
  const filtered = useMemo(() => {
    const list = (products || []).filter((p) => p && p.id);
    if (!query.trim()) return list.slice(0, 25);
    const q = query.trim().toLowerCase();
    return list
      .filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q)
      )
      .slice(0, 25);
  }, [query, products]);

  return (
    <div className="fin-wt-picker">
      <input
        type="text"
        className="fin-wt-picker-search"
        placeholder="Search products by name or SKU"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {loading && <p className="fin-wt-picker-empty">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <p className="fin-wt-picker-empty">No products match.</p>
      )}
      {!loading && filtered.length > 0 && (
        <div className="fin-wt-picker-list">
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`fin-wt-picker-row ${value === p.id ? 'is-selected' : ''}`}
              onClick={() => onChange(p)}
            >
              <span className="fin-wt-picker-name">{p.name}</span>
              <span className="fin-wt-picker-stock">{Number(p.quantity ?? 0)} in stock</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LogWastageSidebar({ isOpen, onClose, onSubmit, products, productsLoading }) {
  const [productQuery, setProductQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('EXPIRED');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset on close so the next open starts fresh.
      const t = setTimeout(() => {
        setProductQuery('');
        setSelected(null);
        setQuantity('');
        setReason('EXPIRED');
        setNotes('');
        setSubmitting(false);
      }, 200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  const canSubmit =
    !!selected &&
    Number(quantity) > 0 &&
    Number(quantity) <= Number(selected.quantity ?? 0);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({
        productId: selected.id,
        quantity: Number(quantity),
        reason,
        notes: notes || undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, onSubmit, selected, quantity, reason, notes, onClose]);

  const footer = (
    <>
      <button
        type="button"
        className="fin-wt-btn fin-wt-btn--ghost"
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        type="button"
        className="fin-wt-btn fin-wt-btn--primary"
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
      >
        {submitting ? 'Logging…' : 'Log wastage'}
      </button>
    </>
  );

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      title="Log wastage"
      subtitle="Record inventory lost to expiry, damage, theft, or recall."
      width="md"
      footer={footer}
    >
      <div className="fin-wt-form">
        <label className="fin-wt-field">
          <span className="fin-wt-field-label">Product</span>
          <ProductPicker
            query={productQuery}
            setQuery={setProductQuery}
            products={products}
            loading={productsLoading}
            value={selected?.id || null}
            onChange={(p) => setSelected(p)}
          />
          {selected && (
            <p className="fin-wt-field-hint">
              Selected: <strong>{selected.name}</strong> ({Number(selected.quantity ?? 0)} in stock)
            </p>
          )}
        </label>

        <label className="fin-wt-field">
          <span className="fin-wt-field-label">Quantity wasted</span>
          <input
            type="number"
            min="1"
            max={selected?.quantity || undefined}
            step="1"
            className="fin-wt-input"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="0"
          />
          {selected && Number(quantity) > Number(selected.quantity ?? 0) && (
            <p className="fin-wt-field-error">
              Can&apos;t exceed available stock ({selected.quantity}).
            </p>
          )}
        </label>

        <label className="fin-wt-field">
          <span className="fin-wt-field-label">Reason</span>
          <div className="fin-wt-reason-grid">
            {REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                className={`fin-wt-reason-pill ${reason === r.value ? 'is-active' : ''}`}
                onClick={() => setReason(r.value)}
              >
                <span className="fin-wt-reason-pill-label">{r.label}</span>
                <span className="fin-wt-reason-pill-hint">{r.hint}</span>
              </button>
            ))}
          </div>
        </label>

        <label className="fin-wt-field">
          <span className="fin-wt-field-label">
            Note <span className="fin-wt-field-optional">(optional)</span>
          </span>
          <textarea
            className="fin-wt-input fin-wt-textarea"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything that'll help you remember this later"
          />
        </label>
      </div>
    </Sidebar>
  );
}

function EmptyState({ onOpenLog }) {
  return (
    <div className="fin-wt-empty">
      <Package size={36} className="fin-wt-empty-icon" />
      <h3 className="fin-wt-empty-title">No wastage logged yet</h3>
      <p className="fin-wt-empty-sub">
        Track expired stock, damage, or theft so your reports show the true cost of inventory loss.
      </p>
      <button type="button" className="fin-wt-btn fin-wt-btn--primary" onClick={onOpenLog}>
        <Plus size={14} />
        <span>Log your first wastage</span>
      </button>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="fin-wt-skeleton-list">
      {[0, 1, 2].map((i) => (
        <div key={i} className="fin-wt-skeleton-row">
          <div className="fin-wt-skeleton-line fin-wt-skeleton-line--wide" />
          <div className="fin-wt-skeleton-line fin-wt-skeleton-line--narrow" />
        </div>
      ))}
    </div>
  );
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function WastageTab() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const { getAxios } = useSecureAuth();
  const { items, summary, loading, error, refetch, create, remove } = useWastage();

  const [showLog, setShowLog] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const formatMoney = useCallback(
    (n) => formatInDisplayCurrency(Number(n || 0), accountCurrency),
    [formatInDisplayCurrency, accountCurrency]
  );

  // Fetch products once when the log Sidebar opens (lazy — most tab
  // visits never click "Log wastage").
  useEffect(() => {
    if (!showLog) return;
    if (products.length > 0) return;
    let cancelled = false;
    setProductsLoading(true);
    (async () => {
      try {
        const axios = getAxios();
        const resp = await axios.get('/merchants/products', { params: { limit: 200 } });
        const list = resp?.data?.products || resp?.data || [];
        if (!cancelled) {
          setProducts(
            Array.isArray(list)
              ? list.filter((p) => p && p.status !== 'DRAFT' && p.deletedAt == null)
              : []
          );
        }
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showLog, products.length, getAxios]);

  const handleCreate = useCallback(
    async (payload) => {
      try {
        await create(payload);
        toast.success('Wastage logged');
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Failed to log wastage');
        throw err;
      }
    },
    [create, toast]
  );

  const handleRemove = useCallback(
    async (row) => {
      const ok = await confirm(
        `Delete this wastage entry? ${Number(row.quantity)} ${row.productName} will be re-added to your stock.`,
        {
          title: 'Delete wastage entry?',
          confirmText: 'Delete',
          cancelText: 'Keep',
          type: 'danger',
        }
      );
      if (!ok) return;
      try {
        await remove(row.id);
        toast.success('Entry deleted; stock restored');
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Failed to delete');
      }
    },
    [confirm, remove, toast]
  );

  return (
    <div className="fin-wt">
      <header className="fin-wt-head">
        <div>
          <h2 className="fin-wt-title">Wastage</h2>
          <p className="fin-wt-subtitle">
            Inventory lost to expiry, damage, theft, or recall — surfaced as a real cost.
          </p>
        </div>
        <button type="button" className="fin-wt-btn fin-wt-btn--primary" onClick={() => setShowLog(true)}>
          <Plus size={14} />
          <span>Log wastage</span>
        </button>
      </header>

      {loading && <SkeletonList />}

      {!loading && error && (
        <div className="fin-wt-error" role="alert">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button type="button" className="fin-wt-error-retry" onClick={refetch}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          {items.length > 0 && <SummaryStrip summary={summary} formatMoney={formatMoney} />}
          {items.length === 0 ? (
            <EmptyState onOpenLog={() => setShowLog(true)} />
          ) : (
            <div className="fin-wt-list">
              {items.map((row) => (
                <WastageRow
                  key={row.id}
                  row={row}
                  onRemove={handleRemove}
                  formatMoney={formatMoney}
                />
              ))}
            </div>
          )}
        </>
      )}

      <LogWastageSidebar
        isOpen={showLog}
        onClose={() => setShowLog(false)}
        onSubmit={handleCreate}
        products={products}
        productsLoading={productsLoading}
      />
    </div>
  );
}
